import { useForm } from "react-hook-form";
import {
  CityPayloadSchema,
  cityPayloadSchema,
  useCity,
  useCityEdit,
} from "@/api/city";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { SubmitButton } from "@/components/ui/button";
import { Loading } from "@/components/ui/loading";
import { CityFormFields } from "./CityFormFields";
import { FormContainer } from "@/components/form/form-container";
import { Anchor } from "@/components/ui/anchor";
import { ErrorMessageFromResponse } from "@/api/types";

export function CityEditForm({ id }: { id: string }) {
  const { data: city, isPending: isLoadingData } = useCity({
    id,
  });
  const form = useForm<CityPayloadSchema>({
    resolver: zodResolver(cityPayloadSchema),
    defaultValues: {
      cityName: city?.cityName,
      cityCode: city?.cityCode,
      province: city?.province,
      ftax: city?.ftax,
      fdtAreaHpm: city?.fdtAreaHpm,
      fibernodeAreaHpm: city?.fibernodeAreaHpm,
      branchHpm: city?.branchHpm,
    },
    values: city,
  });
  const { mutate, isPending } = useCityEdit();
  const navigate = useNavigate();

  const handleSubmit = (values: CityPayloadSchema) => {
    mutate(
      { id: id, data: values },
      {
        onSuccess: () => {
          swalSuccess
            .fire({
              text: "City has been updated",
            })
            .then(() => navigate("/master-data/city"));
        },
        onError: (error) => {
          swalError.fire({
            text:
              (error as ErrorMessageFromResponse).response?.data.data ??
              error.message,
          });
        },
      },
    );
  };

  if (isLoadingData) {
    return <Loading />;
  }

  return (
    <FormContainer form={form} onSubmit={handleSubmit}>
      <CityFormFields />
      <div className="ml-auto flex gap-x-2">
        <Anchor href="/master-data/city" mood="axiata-blue-outline">
          Cancel
        </Anchor>
        <SubmitButton loading={isPending} />
      </div>
    </FormContainer>
  );
}
